/**
 * A list of cards built from data, rebuilt only where the data changed.
 *
 * THIS IS THE REPLACEMENT FOR `ItemsControl` + `DataTemplate`, of which the OpenSilver app has 92.
 * Webwin's list controls are one line per row, virtualized, and have no item template at all, so a
 * CRM card - a heading, three supporting lines, a chip and two buttons - cannot be one of their
 * rows. A card here is therefore a function that builds a control, and this class is the diff that
 * keeps calling it from being a full rebuild.
 *
 * WHY A DIFF AND NOT `clear()` + rebuild. Three reasons, in increasing order of how much they cost:
 *   * a rebuild while a list refreshes destroys the row a technician is interacting with, which is
 *     the `MonitorChanged` bug this codebase already shipped once - a poll result snapping shut a
 *     credential panel mid-typing;
 *   * it loses focus and scroll position on every refresh, and these lists refresh on a timer;
 *   * it is the difference between rebuilding 250 cards and rebuilding the one that changed, on a
 *     canvas where every card is ten controls.
 *
 * IDENTITY IS THE KEY, freshness is `version`. A row is kept when its key is unchanged, and rebuilt
 * when its version differs - so a caller that has nothing cheap to compare can return the whole item
 * (compared by JSON) or omit it entirely and never rebuild a surviving row.
 */
import type { Control, StackPanel } from 'webwin';
import { GAP, vstack } from './layout.js';

export interface CardListOptions<T> {
  /** What makes two renderings of a row the same row. An id, essentially always. */
  key: (item: T) => string | number;
  /** Builds the control for one item. Called once per new key, and again when `version` changes. */
  build: (item: T, index: number) => Control;
  /**
   * What "this row's data changed" means. Defaults to the whole item, compared structurally.
   *
   * A cheap discriminator is better where one exists (`updatedUtc`, a status plus a count), because
   * the default stringifies every item on every refresh - fine for a few hundred rows, and worth
   * replacing on a list that refreshes every four seconds.
   */
  version?: (item: T) => unknown;
  /**
   * The most rows rendered, ever.
   *
   * The OpenSilver pages cap at 250 and tell the reader to filter, for a reason that applies here
   * too: a card is ~10 controls, so a thousand of them is ten thousand laid out on every resize. The
   * cap is a visible one - `overflow` reports what was dropped so the page can say so, because a
   * list silently missing its tail is how a technician concludes a record is gone.
   */
  max?: number;
  spacing?: number;
}

export class CardList<T> {
  /** The stack to add to a page. Measured, stretched: it grows with its rows. */
  readonly panel: StackPanel;

  readonly #options: CardListOptions<T>;
  #rows = new Map<string, { control: Control; version: string }>();
  #items: readonly T[] = [];
  #overflow = 0;

  constructor(options: CardListOptions<T>) {
    this.#options = options;
    this.panel = vstack({ spacing: options.spacing ?? GAP.md });
  }

  /** How many rows the cap dropped on the last `set`. Zero unless `max` bit. */
  get overflow(): number {
    return this.#overflow;
  }

  get count(): number {
    return this.#rows.size;
  }

  /**
   * Renders `items`, reusing every row whose key and version are unchanged.
   *
   * The pass is: build or keep each surviving row into a new map, drop whatever the new map does not
   * claim, then walk the panel putting each row at its index. `moveTo` rather than remove-and-insert
   * so a reorder does not destroy and recreate a control that is perfectly good.
   */
  set(items: readonly T[]): void {
    this.#items = items;
    const max = this.#options.max ?? Number.MAX_SAFE_INTEGER;
    const shown = items.length > max ? items.slice(0, max) : items;
    this.#overflow = items.length - shown.length;

    const version = this.#options.version ?? ((item: T): unknown => item);
    const next = new Map<string, { control: Control; version: string }>();
    const order: Control[] = [];

    shown.forEach((item, index) => {
      const key = String(this.#options.key(item));
      const stamp = JSON.stringify(version(item)) ?? '';
      const existing = this.#rows.get(key);
      if (existing && existing.version === stamp) {
        next.set(key, existing);
        order.push(existing.control);
        this.#rows.delete(key);
        return;
      }
      // Either new or changed. A changed row's old control is dropped below with the rest.
      const control = this.#options.build(item, index);
      next.set(key, { control, version: stamp });
      order.push(control);
    });

    // Whatever is left in the old map is a row that has gone or been superseded.
    for (const stale of this.#rows.values()) this.panel.controls.remove(stale.control);
    this.#rows = next;

    order.forEach((control, index) => {
      const at = this.panel.controls.indexOf(control);
      if (at < 0) this.panel.controls.insert(index, control);
      else if (at !== index) this.panel.controls.moveTo(control, index);
    });

    this.panel.layout();
  }

  /**
   * Re-runs the diff over the items it already has.
   *
   * For state that lives outside the data: an event row's expand toggle, say, where the item is
   * unchanged but its `version` is not. Only the rows whose version really moved are rebuilt, which
   * is exactly what makes a per-row toggle cheap enough to implement this way rather than by reaching
   * into the control the list built.
   */
  refresh(): void {
    this.set(this.#items);
  }

  /** Empties the list. Used when a page switches between two sources rather than refreshing one. */
  clear(): void {
    this.panel.controls.clear();
    this.#rows.clear();
    this.#items = [];
    this.#overflow = 0;
  }
}
